import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  CompatibleWith,
  ConnextData,
  FwBuild,
  GetLatestFirmwareDataQuery,
  isNotNullOrUndefined,
  ProductModelService,
  productProperty,
  System,
} from 'ngx-core-services';
import { Store } from '@ngrx/store';
import { first, map, shareReplay, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { DialogDataComponent, ColumnConfig, ColumnTypeEnum } from 'ngx-zcs-components';
import gql from 'graphql-tag';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { selectedSystemConnextData, SystemsState } from '../../store';
import { ConnextDetailsService } from './connext-details.service';
import { DeviceTable } from '../azzurro-hub-details/azzurro-hub-details.component';

const latestFwVersionGqlQuery = gql`
  fragment firmwaredata on FirmwareData {
    created
    updated
    PK
    SK
    url
    version
    revision
    build
    releaseNotes
}`;

const getLabels = () => ({
  actions: 'Actions',
  devicesTitle: 'Dispositivi Connext',
  activatedSensor: 'Actived Sensor',
  authorization: 'Authorization',
  connection: 'Connection',
  connected: 'Connected',
  disconnected: 'Disconnected',
  availableConnextUpgrade: 'Firmware update available.',
  deviceType: 'Device type',
  device: 'Device',
  fwUpdateDescriptionError: 'Oops, error, try again!',
  fwUpdateDescriptionSuccess:
    'The update process has started successfully. Please wait a few moments until the process is complete. In the meantime, the Connext device will disconnect and reconnect automatically.',
  fwVersion: 'Fw version',
  localeCode: 'en-EN',
  phase: 'Phase',
  plantPower: 'Plant power',
  position: 'Position',
  serialNumber: 'Serial number',
  singlePhase: 'Single phase',
  skip: 'Skip',
  threePhase: 'Three phase',
  timezone: 'Timezone',
  title: 'Device details',
  updateToTheLatestVersion: 'Update the firmware to the latest version.',
  updateFirmware: 'Update Firmware',
  updated: 'No updates available',
  update: 'update',
});

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-550': 550,
})
@Component({
  selector: 'lib-connext-details',
  templateUrl: './connext-details.component.html',
  styleUrls: ['./connext-details.component.scss'],
})
export class ConnextDetailsComponent implements ICustomBlock, IReduxBlock, AfterViewInit {
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;
  @Output() onDeviceSelected = new EventEmitter<string>();

  readonly _externalProperties$ = new BehaviorSubject<
    productProperty[] | undefined
  >(undefined);
  @Input() set externalDevices(properties: productProperty[] | undefined) {
    this._externalProperties$.next(properties);
  }

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  _labels = getLabels();
  readonly labels$ = new BehaviorSubject<typeof this._labels>(this._labels);
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.labels$.next(this._labels);
    this.updateColumnLabels();
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Input() uiFramework: 'material' | 'ionic' = 'material';

  readonly system$ = new BehaviorSubject<System | undefined>(undefined);
  @Input() system?: System;

  readonly isUpdatingFw$ = new BehaviorSubject<boolean>(false);
  readonly _instanceId$: Observable<string> = new Observable<string>();

  columns: ColumnConfig[] = [];

  readonly connextData$: Observable<Partial<ConnextData>> = combineLatest([
    this._refresh$,
    this.store.select(selectedSystemConnextData),
  ]).pipe(
    map(([_, data]) => data),
    isNotNullOrUndefined(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly deviceId$: Observable<string> = this.connextData$.pipe(
    isNotNullOrUndefined(),
    map((data) => data.info?.sn.sn || data.id || ''),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly deviceUid$: Observable<string> = this.connextData$.pipe(
    isNotNullOrUndefined(),
    map((data) => data.id || ''),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly fwCategory$: Observable<number | undefined> = this.connextData$.pipe(
    map((data) => data.info?.fw?.fwCategory),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly fwVersion$: Observable<string | undefined> = this.connextData$.pipe(
    map((data) => data.info?.fw?.fw_ver),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly statusConnection$: Observable<boolean> = this.connextData$.pipe(
    isNotNullOrUndefined(),
    map((data) => data.info?.connection?.status === 'connected'),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly connectionProperties$: Observable<productProperty[]> =
    this.connextData$.pipe(
      map((data) => {
        if (!data?.info?.connection?.status) return [];

        const isConnected = data.info.connection.status === 'connected';

        return [
          {
            name: this.labels.connection,
            value: isConnected
              ? this.labels.connected
              : this.labels.disconnected,
            icon: isConnected ? 'wifi' : 'wifi_off',
            iconCssClasses: isConnected
              ? 'bg-green-400 text-white radius-4'
              : 'bg-red-400 text-white radius-4',
          },
        ];
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  readonly connextDataProperties$: Observable<productProperty[]> =
    combineLatest([this.connextData$, this._externalProperties$]).pipe(
      map(([data, externalProps]) => {
        if (!data) return [];

        const properties: productProperty[] = [];

        if (data.info?.fw?.fw_ver) {
          properties.push({
            name: this.labels.fwVersion,
            value: data.info.fw.fw_ver.toUpperCase(),
            icon: 'memory',
            iconCssClasses: 'text-grey-400',
          });
        }

        const hasActivatedSensor: boolean = data.devices?.some(
          (device: { sn?: string }) => device.sn && /^XX.*X$/.test(device.sn)
        ) || false;

        if (hasActivatedSensor) {
          properties.push({
            name: this.labels.activatedSensor,
            value: this.labels.activatedSensor,
            iconCssClasses: 'text-grey-400',
          });
        }

        if (data.config?.TZ !== undefined) {
          const tz = Number(data.config.TZ) / 4;
          properties.push({
            name: this.labels.timezone,
            value: `UTC ${tz >= 0 ? '+' : ''}${tz.toFixed()}`,
            iconCssClasses: 'text-grey-400',
          });
        }

        /**
         * The `pType` value can only be:
         * - `0`  Single Phase
         * - `1`  Three Phase
         */
        if (data.config?.pType !== undefined) {
          properties.push({
            name: this.labels.phase,
            value:
              data.config.pType === 0
                ? this.labels.singlePhase
                : this.labels.threePhase,
            icon: 'power_input',
            iconCssClasses: 'text-grey-400',
          });
        }

        if (data.config?.maxLoadPwr !== undefined) {
          const maxPlantPower = Number(data.config.maxLoadPwr) / 10;
          properties.push({
            name: this.labels.plantPower,
            value: `${maxPlantPower} KW`,
            icon: 'bolt',
            iconCssClasses: 'text-grey-400',
          });
        }

        if (data.config?.latitude && data.config?.longitude) {
          properties.push({
            name: this.labels.position,
            value: `${data.config.latitude}, ${data.config.longitude}`,
            icon: 'location_on',
            iconCssClasses: 'text-grey-400',
          });
        }

        return externalProps ? [...properties, ...externalProps] : properties;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  readonly isLatestVersion$ = combineLatest([
    this.fwCategory$.pipe(isNotNullOrUndefined()),
    this.fwVersion$.pipe(isNotNullOrUndefined()),
  ]).pipe(
    switchMap(async ([category, currentFwVersion]) => {
      const latestFw = await this.getLatestFwVersion(category);
      return latestFw?.version === currentFwVersion;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly devices$: Observable<DeviceTable[] | null> = this.connextData$.pipe(
    map((data) => {
      const devices: DeviceTable[] = [];
      data.devices?.forEach((d) => {
        const product = new ProductModelService(d.sn);
        devices.push({
          device: product,
          loggerId: data.id || '',
        });
      });

      return devices ?? null;
    }),
  );

  constructor(
    private readonly getLatestFirmwareDataQuery: GetLatestFirmwareDataQuery,
    private readonly connextDetailsService: ConnextDetailsService,
    private readonly dialog: MatDialog,
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    this.updateColumnLabels();
  }

  async onUpdateFirmware() {
    try {
      const deviceUid = await this.deviceUid$.pipe(first()).toPromise();
      const category = await this.fwCategory$.pipe(first()).toPromise();

      if (!deviceUid || category === undefined) {
        return;
      }

      const dialogRef = this.dialog.open(DialogDataComponent, {
        data: {
          title: this.labels.updateFirmware,
          content: this.labels.updateToTheLatestVersion,
          buttons: [
            {
              text: this.labels.skip,
              action: () => dialogRef.close(false),
            },
            {
              text: this.labels.update,
              cssClass: 'custom-mat-button-primary',
              focus: true,
              action: async () => {
                dialogRef.close(true);
                await this.connextDetailsService.executeFirmwareUpdate(
                  deviceUid,
                  category,
                  this.labels,
                  (v: boolean) => this.isUpdatingFw$.next(v)
                );
              },
            },
          ],
        },
      });

      await dialogRef.afterClosed().toPromise();
    } catch (error) {
      console.error('Error', error);
    }
  }

  async getLatestFwVersion(category: number) {
    const response = await this.getLatestFirmwareDataQuery.execute(
      latestFwVersionGqlQuery,
      {
        thingDef: 'connext_v1',
        build: FwBuild.ANY,
        compatibleWith: CompatibleWith(category),
      }
    );

    return response.data.getLatestFirmwareData;
  }

  updateColumnLabels(): void {
    this.columns = [
      {
        columnDef: 'deviceId',
        header: this.labels.device,
        type: ColumnTypeEnum.Text,
        cell: (row: DeviceTable) => `${row.device.id}`,
      },
      {
        columnDef: 'deviceType',
        header: this.labels.deviceType,
        type: ColumnTypeEnum.Text,
        cell: (row: DeviceTable) => row.device.type?.name || 'Inverter',
      },
      {
        columnDef: 'actions',
        header: this.labels.actions,
        type: ColumnTypeEnum.Template,
        template: this.actionsTemplate,
      }
    ];
  }
}
